import { Button } from '@datahub/design-system'
import { User } from 'lucide-react'
import type { SaveState } from './useDraft'

interface PortalTopBarProps {
  contextLine: string
  /** Shown in the company chip on the right, as the Figma frame shows the account. */
  companyName: string
  saveState: SaveState
  /** Shown in place of the generic label when the server refused the save. */
  saveError?: string | null
  onSave: () => void
  onUpload: () => void
  disabled?: boolean
}

const SAVE_LABEL: Record<SaveState, string> = {
  idle: '',
  saving: 'Saving…',
  saved: 'Saved',
  retrying: 'Could not save, retrying…',
  error: 'Could not save. Check your connection.',
  blocked: 'Could not save',
}

/**
 * Company-facing TopBar variant (plan Section 12.5): the search is replaced by the cycle
 * context, and the account button by Save progress and Upload file. Needs Design sign-off
 * (plan Section 12.9, item 1).
 */
export function PortalTopBar({ contextLine, companyName, saveState, saveError, onSave, onUpload, disabled }: PortalTopBarProps) {
  const saveLabel = saveState === 'blocked' && saveError ? saveError : SAVE_LABEL[saveState]
  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex items-stretch bg-background-main" style={{ height: 80 }}>
      <div
        className="flex items-center flex-shrink-0 bg-background-main"
        style={{ width: 256, paddingLeft: 20, paddingRight: 32, borderRight: '0.5px solid #4B5563', borderBottom: '0.5px solid #4B5563' }}
      >
        <img src="/images/stears-logo-white.webp" alt="Stears" style={{ height: 24, width: 'auto' }} />
      </div>
      <div
        className="flex flex-1 items-center justify-between bg-background-main"
        style={{ paddingLeft: 20, paddingRight: 20, borderBottom: '0.5px solid #4B5563' }}
      >
        <div className="flex items-center" style={{ gap: 16, minWidth: 0 }}>
          <span className="font-jakarta text-text-support truncate" style={{ fontSize: 12 }}>{contextLine}</span>
          {saveLabel && (
            <span
              className="font-jakarta flex-shrink-0"
              style={{ fontSize: 12, color: saveState === 'saved' ? '#22C55E' : saveState === 'error' || saveState === 'blocked' ? '#F05042' : '#9CA3AF' }}
              role="status"
              aria-live="polite"
            >
              {saveLabel}
            </span>
          )}
        </div>
        <div className="flex items-center flex-shrink-0" style={{ gap: 10 }}>
          <Button variant="outline" size="medium" onClick={onSave} disabled={disabled}>Save progress</Button>
          <Button variant="filled-grey" size="medium" onClick={onUpload} disabled={disabled}>Upload file</Button>
          {/* Company chip, mirroring the account chip in Figma node 4352:18988. */}
          <div
            className="hidden md:flex items-center"
            style={{ height: 38, padding: '0 14px', gap: 8, backgroundColor: '#2D3036' }}
          >
            <User size={14} className="flex-shrink-0" style={{ color: '#FAFAFA' }} aria-hidden />
            <span className="font-jakarta truncate" style={{ fontSize: 12, color: '#FAFAFA', maxWidth: 160 }}>
              {companyName}
            </span>
          </div>
        </div>
      </div>
    </header>
  )
}
